/**
 * CalendarSelectorModal Component
 *
 * Modal for selecting which calendar to save an event to.
 * Displays available device calendars in a scrollable modal.
 *
 * Follows single responsibility: handles calendar selection UI in modal format.
 */

import { useEffect } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

type CalendarSelectorModalProps = {
  visible: boolean;
  calendars: DeviceCalendar[];
  selectedCalendarId: string | null;
  onSelect: (calendarId: string) => void;
  onClose: () => void;
};

/**
 * CalendarSelectorModal Component
 *
 * Full-screen modal for calendar selection with scrollable list.
 */
export function CalendarSelectorModal({
  visible,
  calendars,
  selectedCalendarId,
  onSelect,
  onClose,
}: CalendarSelectorModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  // Animation values
  const backdropOpacity = useSharedValue(0);
  const translateY = useSharedValue(300);

  useEffect(() => {
    if (visible) {
      backdropOpacity.value = withTiming(1, { duration: 200 });
      translateY.value = withTiming(0, { duration: 250 });
    } else {
      backdropOpacity.value = withTiming(0, { duration: 200 });
      translateY.value = withTiming(300, { duration: 200 });
    }
  }, [visible, backdropOpacity, translateY]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const handleSelect = (calendarId: string) => {
    onSelect(calendarId);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        {/* Animated Backdrop */}
        <Animated.View style={[styles.backdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        {/* Animated Content */}
        <Animated.View
          style={[styles.content, { backgroundColor: palette.background }, contentStyle]}
        >
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: palette.border }]}>
            <Text
              style={[
                styles.headerTitle,
                { color: palette.text, fontFamily: getBodyFont() },
              ]}
            >
              Select Calendar
            </Text>
            <TouchableOpacity onPress={onClose} hitSlop={8}>
              <Ionicons name="close" size={24} color={palette.textMuted} />
            </TouchableOpacity>
          </View>

          {/* Calendar List */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
          >
            {calendars.map((calendar, index) => {
              const isSelected = selectedCalendarId === calendar.id;
              const isLast = index === calendars.length - 1;

              return (
                <TouchableOpacity
                  key={calendar.id}
                  style={[
                    styles.option,
                    !isLast && {
                      borderBottomColor: palette.border,
                      borderBottomWidth: StyleSheet.hairlineWidth,
                    },
                    isSelected && { backgroundColor: `${palette.primary}15` },
                  ]}
                  onPress={() => handleSelect(calendar.id)}
                >
                  <View style={styles.optionContent}>
                    <View
                      style={[
                        styles.colorDot,
                        { backgroundColor: calendar.color ?? palette.primary },
                      ]}
                    />
                    <View style={styles.calendarInfo}>
                      <Text
                        style={[
                          styles.calendarTitle,
                          { color: isSelected ? palette.primary : palette.text },
                          { fontFamily: getBodyFont() },
                          isSelected && styles.calendarTitleSelected,
                        ]}
                        numberOfLines={1}
                      >
                        {calendar.title}
                      </Text>
                      {calendar.source && (
                        <Text
                          style={[
                            styles.calendarSource,
                            { color: palette.textMuted, fontFamily: getBodyFont() },
                          ]}
                          numberOfLines={1}
                        >
                          {calendar.source.name}
                        </Text>
                      )}
                    </View>
                  </View>
                  {isSelected && (
                    <Ionicons name="checkmark" size={22} color={palette.primary} />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    borderTopLeftRadius: BorderRadius.lg,
    borderTopRightRadius: BorderRadius.lg,
    maxHeight: '70%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollContent: {
    paddingBottom: Spacing.xxxl,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    flex: 1,
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: 7, // circular
  },
  calendarInfo: {
    flex: 1,
    gap: Spacing.xxs,
  },
  calendarTitle: {
    fontSize: 16,
  },
  calendarTitleSelected: {
    fontWeight: '600',
  },
  calendarSource: {
    fontSize: 13,
  },
});
